export type ConversationRow = {
  id: string; type: string; display_title: string; last_message_preview: string | null; last_message_sender_name?: string | null; last_message_from_self?: boolean;
  unread: number; mentions: number; notification_count?: number; last_message_seq: string; last_revision: string; last_read_seq?: string;
  archived_at: string | null; pinned: boolean; last_activity_at: string; avatar_key: string | null; peer_user_id?: string | null; peer_avatar_key?: string | null;
};
export type ConversationDetail = {
  id: string; workspace_id: string; type: string; title: string | null; display_title?: string | null; description: string | null; avatar_key: string | null;
  shared_room?: boolean; own_participant_id?: string; can_moderate_messages?: boolean;
  owner_user_id: string | null; history_policy: string; archived_at: string | null; role: string; member_count?: number;
  peer_user_id?: string | null; peer_avatar_key?: string | null;
  membership_epoch_id: string; visible_from_seq: string; last_message_seq: string; last_revision: string; last_read_seq?: string; first_unread_seq?: string | null; pinned?: boolean;
};
export type ConversationPage = { conversations: ConversationRow[]; next_cursor: string | null; has_more: boolean; notification_total?: number };
/** Bind credentials to accountKey and reject BEFORE dispatch if obsolete. Forward
 * options.signal to fetch (including response-body reads); abort must settle the
 * adapter promise. An adapter ignoring abort holds its physical slot until it
 * settles: queued callers time out rather than spawning unbounded replacements.
 * Cancellation is not rollback of a dispatched favorite PUT.
 */
export type ConversationRequest = <T>(accountKey: string, path: string, options?: RequestInit) => Promise<T>;
type Page = { index: number; rows: ConversationRow[]; next: string | null; notificationTotal: number };
class Superseded extends Error {}
type Job = { start: () => void; finish: (error: unknown) => void; controller: AbortController };
/** One physical adapter promise + one replaceable waiting intent. Never release
 * the physical slot merely because a logical deadline/abort has fired. */
class LatestRequest {
  private active: Job | null = null;
  private queued: Job | null = null;
  constructor(private readonly timeoutMs: number) {}
  snapshot() { return { inflight: Number(Boolean(this.active)), queued: Number(Boolean(this.queued)) }; }
  cancel() {
    const queued = this.queued; this.queued = null;
    queued?.finish(new Superseded());
    this.active?.finish(new Superseded());
  }
  run<T>(request: (signal: AbortSignal) => Promise<T>): Promise<T> {
    this.cancel();
    return new Promise<T>((resolve, reject) => {
      const controller = new AbortController(); let settled = false;
      const finish = (error: unknown) => {
        if (settled) return;
        settled = true; clearTimeout(timer);
        if (this.queued === job) this.queued = null;
        reject(error); controller.abort(error);
      };
      const release = () => {
        if (this.active === job) this.active = null;
        const next = this.queued; this.queued = null;
        if (next) { this.active = next; next.start(); }
      };
      const job: Job = { controller, finish, start: () => {
        let result: Promise<T>;
        try { result = request(controller.signal); } catch (error) { finish(error); release(); return; }
        void Promise.resolve(result).then(value => {
          if (!settled) { settled = true; clearTimeout(timer); resolve(value); }
          release();
        }, error => { finish(error); release(); });
      } };
      // Includes queue residence, so a broken adapter cannot leave user calls hanging.
      const timer = setTimeout(() => finish(Object.assign(new Error('Не удалось дождаться ответа при загрузке чата. Повторите попытку.'), { code: 'REQUEST_TIMEOUT' })), this.timeoutMs);
      if (this.active) this.queued = job;
      else { this.active = job; job.start(); }
    });
  }
}

/** Live keyset order is authoritative: favorite DESC, activity DESC, UUID DESC.
 * Never re-sort timestamps in JS (the cursor preserves PostgreSQL microseconds).
 * Previous replays from the head with bounded memory, not an unbounded cursor stack.
 */
export class ConversationPages {
  readonly limit: number;
  readonly maxRecords: number;
  private readonly pageCapacity: number;
  private account: string | null = null;
  private generation = 0;
  private pages: Page[] = [];
  private loading = false;
  private q = '';
  private accountGeneration = 0;
  private selectionGeneration = 0;
  private favoriteGeneration = 0;
  private activeId: string | null = null;
  private current: ConversationDetail | null = null;
  private confirmedRead: { id: string; seq: bigint; epoch: string } | null = null;
  // refresh() clears `pages` before the fresh head arrives (every live WS event routes
  // through it via loadConversations()). Without this, the sidebar list would blank on
  // every single background refresh, not just an explicit user action. Serve the last
  // known rows while a refresh is in flight instead; setAccount() clears it explicitly
  // so one account's list can never leak into another's loading state.
  private lastRows: ConversationRow[] = [];
  private counterPage: Page | null = null;
  private failedHead = false;
  private lastPaging = { hasMore: false, canPrevious: false };
  private readonly pageRequests: LatestRequest;
  private readonly detailRequests: LatestRequest;
  private readonly favoriteRequests: LatestRequest;
  private readonly onDenied?: (id: string) => void;
  constructor(private readonly request: ConversationRequest, options: { limit?: number; maxRecords?: number; requestTimeoutMs?: number; onDenied?: (id: string) => void } = {}) {
    this.onDenied = options.onDenied;
    const timeout = options.requestTimeoutMs ?? 15_000;
    if (!Number.isFinite(timeout) || timeout < 1 || timeout > 60_000) throw new Error('Invalid request timeout');
    this.pageRequests = new LatestRequest(timeout);
    this.detailRequests = new LatestRequest(timeout);
    this.favoriteRequests = new LatestRequest(timeout);
    this.limit = Math.min(100, Math.max(1, Math.floor(options.limit ?? 50)));
    this.maxRecords = Math.min(200, Math.max(this.limit, Math.floor(options.maxRecords ?? 200)));
    if (!Number.isFinite(this.limit) || !Number.isFinite(this.maxRecords)) throw new Error('Invalid pagination bounds');
    this.pageCapacity = Math.floor(this.maxRecords / this.limit);
  }
  workload() {
    return { page: this.pageRequests.snapshot(), detail: this.detailRequests.snapshot(), favorite: this.favoriteRequests.snapshot() };
  }
  snapshot() {
    const rows = this.pages.flatMap(p => p.rows).map(r => ({ ...r }));
    // Retain same-query rows during a refresh or transient failure; successful empty is real.
    const retained = !rows.length && (this.loading || this.failedHead);
    const visible = retained ? this.lastRows.map(r => ({ ...r })) : rows;
    const read = this.confirmedRead;
    let notificationTotal = this.counterPage?.notificationTotal ?? 0;
    const counterRow = read && this.counterPage?.rows.find(row => row.id === read.id);
    if (read && counterRow && this.current?.membership_epoch_id === read.epoch && /^\d+$/.test(counterRow.last_message_seq) && BigInt(counterRow.last_message_seq) <= read.seq) {
      notificationTotal = Math.max(0, notificationTotal - (counterRow.notification_count ?? 0));
    }
    if (read && this.current?.id === read.id && this.current.membership_epoch_id === read.epoch) {
      for (const row of visible) if (row.id === read.id && /^\d+$/.test(row.last_message_seq) && BigInt(row.last_message_seq) <= read.seq) {
        row.unread = 0; row.mentions = 0; row.notification_count = 0;
      }
    }
    // Paging follows the rows on screen for the same reason: recomputing it from the emptied pages
    // made "Загрузить ещё"/"Показать предыдущие" disappear and come back on every refresh, which
    // resizes the list under the reader.
    const hasMore = this.failedHead ? false : retained ? this.lastPaging.hasMore : Boolean(this.pages.at(-1)?.next);
    const canPrevious = this.failedHead ? false : retained ? this.lastPaging.canPrevious : (this.pages[0]?.index ?? 0) > 0;
    if (!retained) { this.lastRows = this.pages.flatMap(p => p.rows).map(r => ({ ...r })); this.lastPaging = { hasMore, canPrevious }; }
    // Judged against what is actually on screen, not against `pages`: a refresh empties `pages`
    // for the length of a round trip while `visible` still shows the retained rows, and deciding
    // from `pages` made the "open chat is outside this page" row blink in on every refresh --
    // which, since a sent message refreshes the sidebar, was every message.
    return { notificationTotal, activeId: this.activeId, current: this.current ? { ...this.current } : null, currentOutsideWindow: Boolean(this.current && !visible.some(r => r.id === this.activeId)), rows: visible, hasMore, canPrevious, loading: this.loading, q: this.q };
  }
  async setAccount(accountKey: string | null): Promise<void> {
    ++this.accountGeneration; ++this.selectionGeneration; this.activeId = null; this.current = null; this.confirmedRead = null;
    this.favoriteRequests.cancel(); this.lastRows = []; this.lastPaging = { hasMore: false, canPrevious: false };
    this.counterPage = null; this.account = accountKey; this.q = ''; await this.refresh();
  }
  async refresh(onValidated?: (authorized: boolean) => void): Promise<void> {
    ++this.generation; this.pages = []; this.failedHead = false; this.loading = false; this.pageRequests.cancel();
    const validation = this.select(this.activeId).then(result => { onValidated?.(result === 'authorized'); return result; }, error => { onValidated?.(false); throw error; });
    const account = this.account; const epoch = this.accountGeneration; const generation = this.generation;
    const [pageResult, validationResult] = await Promise.allSettled([this.loadMore(), validation]);
    if (validationResult.status === 'fulfilled' && validationResult.value === 'denied' && this.account === account && this.accountGeneration === epoch && this.generation === generation + 1 && this.activeId === null) {
      // The concurrent head may have been authorized before revocation committed.
      // Discard also an already-retained head and its cursor before restarting.
      this.pages = []; this.loading = false;
      await this.loadMore();
      return;
    }
    if (pageResult.status === 'rejected') throw pageResult.reason;
    if (validationResult.status === 'rejected') throw validationResult.reason;
  }
  /** The reader has caught up in this chat: clear its counters at once, before the next head
   * refresh confirms it, so the badge does not sit there after the messages have been read. */
  markRead(id: string, seq: string): void {
    if (this.current?.id !== id || !/^\d+$/.test(seq)) return;
    const previous = this.confirmedRead;
    const confirmed = previous?.id === id && previous.epoch === this.current.membership_epoch_id && previous.seq > BigInt(seq) ? previous.seq : BigInt(seq);
    this.confirmedRead = { id, seq: confirmed, epoch: this.current.membership_epoch_id };
  }
  async setQuery(query: string): Promise<void> {
    const q = query.trim(); if (q.length > 200) throw new Error('Search exceeds 200 characters');
    if (q !== this.q) { this.lastRows = []; this.lastPaging = { hasMore:false, canPrevious:false }; }
    this.q = q; await this.refresh();
  }
  async setFavorite(id: string, pinned: boolean): Promise<void> {
    if (!this.account) return;
    const account = this.account; const epoch = this.accountGeneration; const mutation = ++this.favoriteGeneration;
    // Fence old-order responses immediately, but retain the bounded visible
    // window while the write is pending. Success replaces it with a fresh head.
    ++this.generation; ++this.selectionGeneration; this.current = null; this.loading = false;
    this.pageRequests.cancel(); this.detailRequests.cancel();
    try {
      await this.favoriteRequests.run(signal => this.request<{ id: string; pinned: boolean }>(account, `/v1/conversations/${encodeURIComponent(id)}/favorite`, { method: 'PUT', body: JSON.stringify({ pinned }), signal }));
    } catch (error) { if (!(error instanceof Superseded) && epoch === this.accountGeneration && mutation === this.favoriteGeneration) throw error; return; }
    if (epoch === this.accountGeneration && mutation === this.favoriteGeneration) await this.refresh();
  }
  async select(id: string | null): Promise<'authorized' | 'denied' | 'none' | 'stale'> {
    const selection = ++this.selectionGeneration; const generation = this.generation;
    if (id !== this.activeId) this.confirmedRead = null;
    this.activeId = this.account ? id : null;
    this.detailRequests.cancel();
    // refresh() re-validates the SAME already-active conversation on every live event, not
    // only an actual navigation. Clearing `current` unconditionally here made a select() call
    // that later resolves 'stale' (superseded by a newer one) wipe out whatever the actually
    // latest, already-authorized call had just set, under real network latency where these
    // overlap -- a real user saw "Не удалось подтвердить доступ к чату" on every message from
    // exactly this race. Only a definitive outcome (authorized/denied/none) should touch it.
    if (!this.account || !id) { this.current = null; return 'none'; }
    const account = this.account;
    const valid = () => selection === this.selectionGeneration && generation === this.generation;
    try {
      const detail = await this.detailRequests.run(signal => this.request<ConversationDetail>(account, `/v1/conversations/${encodeURIComponent(id)}`, { signal }));
      if (!valid()) return 'stale';
      if (detail.id !== id) throw new Error('Mismatched current conversation');
      if (this.confirmedRead?.epoch !== detail.membership_epoch_id) this.confirmedRead = null;
      this.current = { ...detail };
      return 'authorized';
    } catch (error) {
      if (!valid()) return 'stale';
      const refusal = error as { code?: string; status?: number };
      if (refusal?.code === 'NOT_FOUND' || refusal?.code === 'FORBIDDEN' || refusal?.status === 404 || refusal?.status === 403) {
        this.activeId = null; this.current = null; this.confirmedRead = null; this.counterPage = null;
        ++this.generation; this.loading = false;
        this.pageRequests.cancel();
        this.pages = this.pages.map(p => ({ ...p, rows: p.rows.filter(r => r.id !== id) }));
        this.lastRows = this.lastRows.filter(row => row.id !== id);
        // Publish revocation immediately, independently of refresh()'s concurrent list request.
        // Only the latest account/selection reaches here; aborted and stale calls cannot revoke it.
        this.onDenied?.(id);
        return 'denied';
      } else throw error;
    }
  }
  private async fetchPage(account: string, index: number, cursor: string | null): Promise<Page> {
    const params = new URLSearchParams({ limit: String(this.limit) });
    if (this.q) params.set('q', this.q);
    if (cursor) params.set('cursor', cursor);
    const result = await this.pageRequests.run(signal => this.request<ConversationPage>(account, `/v1/conversations?${params}`, { signal }));
    if (!result || !Array.isArray(result.conversations)) throw Object.assign(new Error('Сервер вернул некорректный список чатов. Повторите попытку.'), { code:'INVALID_RESPONSE' });
    if (result.conversations.length > this.limit || (result.has_more && (!result.next_cursor || result.next_cursor === cursor))) throw new Error('Invalid pagination response');
    return { index, notificationTotal: Number.isSafeInteger(result.notification_total) && result.notification_total! >= 0 ? result.notification_total! : 0, rows: result.conversations.map(r => ({ ...r })), next: result.has_more ? result.next_cursor : null };
  }
  private retain(pages: Page[], page: Page): Page[] {
    // Last response wins for IDs moved across the live anchor. Refresh discovers
    // omissions caused by concurrent activity; traversal is not a snapshot.
    const ids = new Set(page.rows.map(r => r.id));
    const unique = [...new Map(page.rows.map(r => [r.id, r])).values()];
    return [...pages.map(p => ({ ...p, rows: p.rows.filter(r => !ids.has(r.id)) })), { ...page, rows: unique }].slice(-this.pageCapacity);
  }
  async loadMore(): Promise<void> {
    if (!this.account || this.loading || (this.pages.length && !this.pages.at(-1)?.next)) return;
    const account = this.account; const generation = this.generation; this.loading = true;
    const last = this.pages.at(-1);
    try {
      const page = await this.fetchPage(account, last ? last.index + 1 : 0, last?.next ?? null);
      if (generation === this.generation) { this.pages = this.retain(this.pages, page); this.counterPage = page; this.failedHead = false; if (!page.rows.length && !last) { this.lastRows = []; this.lastPaging = { hasMore:false, canPrevious:false }; } }
    } catch (error) {
      if (generation !== this.generation) return;
      const status = (error as {status?:number})?.status;
      if (status === 401 || status === 403 || status === 404) {
        this.counterPage = null; this.pages = []; this.lastRows = []; this.lastPaging = { hasMore:false, canPrevious:false }; this.failedHead = false;
      } else if (!last) this.failedHead = !status || status >= 500 || status === 200 || status === 429;
      if (last?.next && this.invalidCursor(error)) await this.refresh();
      else throw error;
    }
    finally { if (generation === this.generation) this.loading = false; }
  }
  private invalidCursor(error: unknown): boolean {
    return (error as { code?: string })?.code === 'VALIDATION_ERROR' || (error as { status?: number })?.status === 400;
  }
  async previous(): Promise<void> {
    const count = this.pages[0]?.index ?? 0;
    if (!this.account || this.loading || !count) return;
    const account = this.account; const generation = this.generation; this.loading = true;
    this.pages = []; // Do not retain the old window alongside the replay window.
    let replay: Page[] = []; let cursor: string | null = null;
    try {
      for (let index = 0; index < count; index++) {
        const page = await this.fetchPage(account, index, cursor);
        if (generation !== this.generation) return;
        replay = this.retain(replay, page); cursor = page.next;
        if (!cursor) break;
      }
      this.pages = replay; this.counterPage = replay.at(-1) ?? null;
    } catch (error) {
      if (generation !== this.generation) return;
      if (cursor && this.invalidCursor(error)) await this.refresh();
      else throw error;
    }
    finally { if (generation === this.generation) this.loading = false; }
  }
}
